'use client';

import { useRef } from 'react';
import Image from 'next/image';
import { motion, useScroll, useTransform } from 'framer-motion';
import { TbArrowDown, TbSparkles } from 'react-icons/tb';

interface IndustryStory {
    title: string;
    description?: string | null;
    image: string;
}

interface IndustryEditorialReelProps {
    locale: string;
    title: string;
    subtitle: string;
    industries: IndustryStory[];
}

const widerSectors = {
    en: ['Government', 'Real Estate', 'Healthcare', 'Retail', 'Education', 'Technology', 'Events', 'Professional Services'],
    ar: ['القطاع الحكومي', 'العقارات', 'الرعاية الصحية', 'التجزئة', 'التعليم', 'التكنولوجيا', 'الفعاليات', 'الخدمات المهنية'],
};

function PhotoCard({
    story,
    index,
    total,
    progress,
}: {
    story: IndustryStory;
    index: number;
    total: number;
    progress: ReturnType<typeof useScroll>['scrollYProgress'];
}) {
    const start = index / Math.max(total, 1);
    const end = Math.min(1, start + 0.62);
    const y = useTransform(progress, [Math.max(0, start - 0.2), end], [index === 0 ? 10 : 120, index === 0 ? -35 : 0]);
    const rotate = useTransform(progress, [Math.max(0, start - 0.2), end], [index === 0 ? -6 : 7, index === 0 ? -2 : 2]);
    const scale = useTransform(progress, [Math.max(0, start - 0.2), end], [index === 0 ? 0.96 : 0.86, 1]);

    /*
     * Both cards used to be absolutely stacked with only 128px of horizontal
     * separation between 390px-wide cards — a 262px overlap. Card 01 sat behind
     * card 02 with its entire caption covered, so the section read as having one
     * industry rather than two.
     *
     * Below lg they are now ordinary flow items, each fully visible. From lg they
     * return to the overlapped editorial arrangement, but offset far enough
     * (288px apart) that each caption clears the other card.
     */
    return (
        <motion.article
            style={{ y, rotate, scale }}
            className={`relative mx-auto aspect-[4/5] w-full max-w-[360px] overflow-hidden rounded-[2rem] border-4 border-white bg-[#081821] shadow-[0_32px_80px_rgba(8,24,33,0.28)] lg:absolute lg:inset-x-0 lg:w-[300px] lg:max-w-none ${
                index === 0
                    ? 'lg:top-0 lg:z-10 lg:-translate-x-[9rem]'
                    : 'lg:top-44 lg:z-20 lg:translate-x-[9rem]'
            }`}
        >
            <Image
                src={story.image}
                alt={story.title}
                fill
                sizes="(max-width: 1024px) 80vw, 390px"
                className="object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#081821] via-[#081821]/15 to-transparent" />
            <div className="absolute inset-x-0 bottom-0 p-7 text-white">
                <span className="mb-3 inline-flex rounded-full bg-brand-text px-3 py-1 font-heading text-xs font-black uppercase tracking-[0.18em] text-[#081821]">
                    0{index + 1}
                </span>
                <h3 className="font-heading text-3xl font-black">{story.title}</h3>
                {story.description && (
                    <p className="mt-3 line-clamp-3 text-sm leading-relaxed text-white/72">
                        {story.description}
                    </p>
                )}
            </div>
        </motion.article>
    );
}

export default function IndustryEditorialReel({
    locale,
    title,
    subtitle,
    industries,
}: IndustryEditorialReelProps) {
    const isAr = locale === 'ar';
    const sectionRef = useRef<HTMLElement>(null);
    const { scrollYProgress } = useScroll({
        target: sectionRef,
        offset: ['start start', 'end end'],
    });
    const headingY = useTransform(scrollYProgress, [0, 1], [0, -80]);
    const ribbonX = useTransform(scrollYProgress, [0, 1], [isAr ? '8%' : '-8%', isAr ? '-18%' : '18%']);
    const stories = industries.length
        ? industries.slice(0, 2)
        : [
            {
                title: isAr ? 'الأغذية والمشروبات' : 'Food & Beverage',
                description: subtitle,
                image: '/media/industry-food-beverage-hero.webp',
            },
            {
                title: isAr ? 'الضيافة' : 'Hospitality',
                description: subtitle,
                image: '/media/industry-hospitality-hero.webp',
            },
        ];

    return (
        <section
            ref={sectionRef}
            className="relative min-h-[190vh] overflow-clip bg-[#f6f0e5] text-[#081821]"
            dir={isAr ? 'rtl' : 'ltr'}
        >
            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_15%_15%,rgba(255,181,62,0.32),transparent_28rem),radial-gradient(circle_at_85%_72%,rgba(34,131,186,0.16),transparent_30rem)]" />
            {/* Extra top padding below lg. On large screens the content is centred
                in a box taller than it needs, so it never reaches the navbar. On a
                phone the content is taller than the box, so it starts at the padding
                edge — and once the section was pulled up under the fixed navbar,
                64px put the eyebrow badge behind the logo. */}
            <div className="sticky top-20 flex min-h-[calc(100vh-5rem)] items-center overflow-hidden px-5 pb-16 pt-28 md:px-12 lg:px-20 lg:pt-16">
                <motion.div
                    aria-hidden="true"
                    style={{ y: headingY }}
                    className="pointer-events-none absolute -bottom-10 start-0 select-none font-heading text-[26vw] font-black leading-none text-[#081821]/[0.035]"
                >
                    nibras
                </motion.div>

                <div className="relative mx-auto grid w-full max-w-7xl gap-14 lg:grid-cols-[0.88fr_1.12fr] lg:items-center">
                    <motion.div style={{ y: headingY }} className="relative z-30 max-w-2xl">
                        <p className="mb-5 inline-flex items-center gap-2 rounded-full bg-[#081821] px-4 py-2 font-heading text-xs font-black uppercase tracking-[0.18em] text-brand-text">
                            <TbSparkles className="h-4 w-4" />
                            {isAr ? 'من الطاولة إلى المدينة' : 'From the table to the city'}
                        </p>
                        <h1 className="font-heading text-5xl font-black leading-[0.98] md:text-7xl">
                            {title}
                        </h1>
                        <p className="mt-7 max-w-xl border-s-4 border-brand-purple ps-6 text-lg leading-relaxed text-[#081821]/68 md:text-xl">
                            {subtitle}
                        </p>
                        <div className="mt-8 inline-flex items-center gap-3 font-heading text-sm font-bold text-brand-purple">
                            <span>{isAr ? 'مرر لتتسع الصورة' : 'Scroll to widen the picture'}</span>
                            <TbArrowDown className="h-5 w-5 animate-bounce" />
                        </div>

                        {/* The small-screen stand-in for the marquee above. Same
                            sectors, wrapped and in flow, so they read normally
                            instead of running off the edge of the viewport. */}
                        <ul className="mt-8 flex flex-wrap gap-2 lg:hidden">
                            {widerSectors[isAr ? 'ar' : 'en'].map((sector) => (
                                <li
                                    key={sector}
                                    className="rounded-full border border-[#081821]/12 bg-white/80 px-4 py-2 font-heading text-xs font-black text-[#081821] shadow-sm"
                                >
                                    {sector}
                                </li>
                            ))}
                        </ul>
                    </motion.div>

                    <div className="relative z-20 flex flex-col gap-8 lg:block lg:min-h-[680px]">
                        {stories.map((story, index) => (
                            <PhotoCard
                                key={story.title}
                                story={story}
                                index={index}
                                total={stories.length}
                                progress={scrollYProgress}
                            />
                        ))}
                    </div>
                </div>

                {/* lg and up only. The ribbon is pinned to the bottom of the sticky
                    box, which on phones grows past the viewport once the heading and
                    the stacked cards are in it — so the row ended up below the fold
                    with a single half-pill poking out behind the assistant button.
                    The same sectors are listed as a static wrapped row inside the
                    heading column on small screens, so nothing is lost. */}
                <motion.div
                    style={{ x: ribbonX }}
                    className="absolute inset-x-[-12%] bottom-5 hidden w-max items-center gap-3 whitespace-nowrap lg:flex"
                >
                    {[...widerSectors[isAr ? 'ar' : 'en'], ...widerSectors[isAr ? 'ar' : 'en']].map((sector, index) => (
                        <span
                            key={`${sector}-${index}`}
                            className="rounded-full border border-[#081821]/12 bg-white/80 px-5 py-3 font-heading text-sm font-black text-[#081821] shadow-sm backdrop-blur"
                        >
                            {sector}
                        </span>
                    ))}
                </motion.div>
            </div>
        </section>
    );
}
